import { StatCell } from '@workspace/ui/components/stat-cell'
import { ArrowRight, Loader2, Lock, Sparkles } from 'lucide-react'

/**
 * Pre-purchase landing hero shown when the matcher has produced results but no
 * campaign is live yet.
 *
 * The CTA starts outreach rather than linking anywhere. It used to link to the
 * outreach page — which is the page it is rendered on, so clicking it did
 * nothing at all.
 */
export function OutreachAcquisitionHero({
  matchesCount,
  contactsCount,
  contactsEstimated = false,
  noMatches = false,
  onStart,
  isStarting = false,
  startError = null,
}: {
  matchesCount: number
  contactsCount: number
  contactsEstimated?: boolean
  /** Nothing to reach yet — the one honest reason to withhold the CTA. */
  noMatches?: boolean
  onStart: () => void
  isStarting?: boolean
  startError?: string | null
}) {
  return (
    <header className="mb-10 rounded-2xl border border-border/40 bg-gradient-to-br from-card/40 to-card/10 px-7 py-8">
      <div className="max-w-2xl">
        <div className="mb-2 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
          <Sparkles className="size-3" />
          Outreach · automated
        </div>
        <h1 className="text-[34px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
          Your matched investors. <span className="text-primary">Reached for you.</span>
        </h1>
        <p className="mt-3 text-[14.5px] leading-relaxed text-foreground/80">
          Personalized LinkedIn DMs sent from your account. You only pay when investors and founders
          actually engage.
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border/40 bg-border/40 md:grid-cols-3">
        <StatCell
          value={String(matchesCount)}
          label="Investors matched"
          sub="Funds whose thesis fits your stage and sector."
        />
        <StatCell
          value={contactsEstimated ? `~${contactsCount}` : String(contactsCount)}
          label="Contacts to reach"
          sub={
            contactsEstimated
              ? 'Estimated. We are still finding GPs, Principals and founders across the matched funds and portfolio companies.'
              : 'GPs, Principals and founders across the matched funds and portfolio companies.'
          }
        />
        <HeroCtaCell
          noMatches={noMatches}
          onStart={onStart}
          isStarting={isStarting}
          startError={startError}
        />
      </div>
    </header>
  )
}

function HeroCtaCell({
  noMatches,
  onStart,
  isStarting,
  startError,
}: {
  noMatches: boolean
  onStart: () => void
  isStarting: boolean
  startError: string | null
}) {
  // Only withheld when there is genuinely nobody to reach. Access is not a
  // reason: a founder looking at this page can start from it.
  if (noMatches) {
    return (
      <div className="flex cursor-not-allowed flex-col bg-muted/30 px-5 py-5 text-left">
        <div className="flex items-center gap-2">
          <Lock className="size-4 text-muted-foreground" />
          <span className="text-[20px] font-bold leading-none tracking-[-0.01em] text-muted-foreground">
            No matches yet
          </span>
        </div>
        <p className="mt-3 text-[12.5px] font-medium text-muted-foreground">
          Sorry, we haven't found matching investors for your startup yet. Outreach will unlock once
          your first matches arrive.
        </p>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onStart}
      disabled={isStarting}
      className="group flex flex-col bg-primary px-5 py-5 text-left transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-80"
    >
      <div className="flex items-center gap-2">
        <span className="text-[26px] font-bold leading-none tracking-[-0.01em] text-primary-foreground">
          {isStarting ? 'Starting…' : 'Start outreach'}
        </span>
        {isStarting ? (
          <Loader2 className="size-5 animate-spin text-primary-foreground" />
        ) : (
          <ArrowRight className="size-5 text-primary-foreground transition-transform group-hover:translate-x-1" />
        )}
      </div>
      <p className="mt-3 text-[12.5px] font-medium text-primary-foreground/85">
        {startError ?? 'Your first 30 days are free, then charged only when contacts engage.'}
      </p>
    </button>
  )
}
